import { useFormContext } from "#src/contexts/FormSchemaContext";
import {
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { moveItem } from "#src/utils/moveItem";
import {
  Accordion,
  Button,
  Form,
  Panel,
  useFieldArray,
  useForm,
} from "@validereinc/common-components";
import {
  ReorderDirection,
  type ReorderDirectionType,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { get } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { CreateFormTemplateMetricTiles } from "./CreateFormTemplateMetricTiles";
import { CreateFormTemplateQuestionPreviewTable } from "./CreateFormTemplateQuestionPreviewTable";
import { CreateFormTemplateSection } from "./CreateFormTemplateSection";
import styles from "./CreateFormTemplateSectionsStep.module.scss";
import { CreateFormTemplateSubmissionDeduplicationKeyTable } from "./CreateFormTemplateSubmissionDeduplicationKeyTable";
import { DeleteSectionDialog } from "./DeleteSectionDialog";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

const SECTIONS_STEP_TITLE = "Sections & Questions";
const STEP_NUMBER = 2;

export const CreateFormTemplateSectionStep = () => {
  const {
    schema: { data: templateToEdit },
  } = useFormContext();

  const { currentStep, updateStep } = useMultiStepFormContext();

  const getNewSection = () => ({
    description: "",
    id: uuid(),
    name: "",
    is_repeatable: false,
    questions: [],
  });

  const defaultSectionValue = useMemo(
    () =>
      templateToEdit ? templateToEdit.config?.sections[0] : getNewSection(),
    [templateToEdit]
  );

  const form = useForm({
    defaultValues: {
      question_preview: templateToEdit?.question_preview ?? [],
      submission_deduplication_keys:
        templateToEdit?.submission_deduplication_keys ?? [],
      config: {
        questions: templateToEdit?.config?.questions ?? {},
        sections: templateToEdit?.config?.sections ?? [defaultSectionValue],
      },
    },
  });

  const [sectionIndexToDelete, setSectionIndexToDelete] = useState<number>(-1);

  const { fields, append, move, remove } = useFieldArray({
    control: form.control,
    name: "config.sections",
    // we need keyName to prevent RHF from overwriting the id we have already created
    keyName: "rhf_id",
  });

  const sectionArray = form.watch("config.sections");

  const onAddSection = () => {
    const newSection = getNewSection();
    append(newSection);
  };

  const onMoveSection = (
    indexToSwapFrom: number,
    direction: ReorderDirectionType
  ) => {
    const sectionIds = sectionArray.map((section) => section.id);
    moveItem(sectionIds, move, indexToSwapFrom, direction);
  };

  const onRemoveSection = (index: number) => {
    // Remove questions from question_preview and submission_deduplication_keys
    const section = sectionArray[index];
    const sectionQuestionIds: string[] = section.questions;
    const questionPreview = form.watch("question_preview");
    const newQuestionPreview = questionPreview.filter(
      (preview) => !sectionQuestionIds.includes(preview.question_id)
    );
    form.setValue("question_preview", newQuestionPreview);
    const submissionDeduplicationKeys = form.watch(
      "submission_deduplication_keys"
    );
    const newSubmissionDeduplicationKeys = submissionDeduplicationKeys.filter(
      (submissionDeduplicationKey) =>
        !sectionQuestionIds.includes(submissionDeduplicationKey.question_id)
    );
    form.setValue(
      "submission_deduplication_keys",
      newSubmissionDeduplicationKeys
    );

    // Remove all questions from the section in config.questions
    const questions = form.watch("config.questions");
    const newQuestions = Object.fromEntries(
      Object.entries(questions).filter(
        ([questionId]) => !sectionQuestionIds.includes(questionId)
      )
    );
    form.setValue("config.questions", newQuestions);

    remove(index);
  };

  const getFormState = useMemoizedFormState(form);

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState,
    });
  }, [getFormState]);

  return (
    <>
      {currentStep === STEP_NUMBER ? (
        <>
          <Form {...form}>
            <Panel title={SECTIONS_STEP_TITLE}>
              <CreateFormTemplateMetricTiles />

              <Panel>
                {fields.map((field, index) => (
                  <Accordion
                    key={field.id}
                    defaultActiveKeys={[field.id]}
                  >
                    <AccordionPanel
                      key={field.id}
                      dataKey={field.id}
                      title={`Section ${index + 1}${
                        sectionArray[index]?.name
                          ? `: ${sectionArray[index]?.name}`
                          : ""
                      }`}
                      actionRow={[
                        ...(index !== 0
                          ? [
                              <Button
                                key={`move-section-up-${index}-button`}
                                icon="arrow-circle-up"
                                onClick={() =>
                                  onMoveSection(index, ReorderDirection.UP)
                                }
                              />,
                            ]
                          : []),
                        ...(index !== fields.length - 1
                          ? [
                              <Button
                                key={`move-section-down-${index}-button`}
                                icon="arrow-circle-down"
                                onClick={() =>
                                  onMoveSection(index, ReorderDirection.DOWN)
                                }
                              />,
                            ]
                          : []),
                        ...(fields?.length > 1
                          ? [
                              <Button
                                key={`remove-section-${index}-button`}
                                icon="trash"
                                variant="error-outline"
                                onClick={() => setSectionIndexToDelete(index)}
                              />,
                            ]
                          : []),
                      ]}
                      error={get(
                        form.formState.errors,
                        `config.sections.${index}`
                      )}
                    >
                      <CreateFormTemplateSection
                        index={index}
                        sectionName={
                          sectionArray[index]?.name
                            ? `${sectionArray[index]?.name}`
                            : ""
                        }
                      />
                    </AccordionPanel>
                  </Accordion>
                ))}
              </Panel>

              <Button
                onClick={onAddSection}
                icon="plus-circle"
              >
                Create Section
              </Button>
              <DeleteSectionDialog
                isOpen={sectionIndexToDelete > -1}
                onClose={() => {
                  setSectionIndexToDelete(-1);
                }}
                handleDeleteSection={onRemoveSection}
                sectionIndexToDelete={sectionIndexToDelete}
              />
            </Panel>
            <div className={cx("table-container")}>
              <CreateFormTemplateSubmissionDeduplicationKeyTable />
              <CreateFormTemplateQuestionPreviewTable />
            </div>
          </Form>
        </>
      ) : null}
    </>
  );
};
